/**
 * JBIG2 encoder (WebAssembly), the part that runs anywhere: the site loads it in the browser (jbig2.ts), the API in Node. Built from jbig2enc (Apache-2.0) and
 * Leptonica (BSD-2); the recipe is in tools/jbig2-wasm. JBIG2 stores black-and-white pages about 35% smaller than CCITT G4 when each
 * page is coded on its own (lossless), and about 60% smaller in symbol mode, where every letter shape is stored once
 * and reused across pages.
 *
 * Bitmaps are packed rows, most significant bit first, 1 = black, each row (width + 7) / 8 bytes.
 */

export interface Jbig2Encoder {
  /** One page coded on its own, exactly (lossless for the bitmap given). */
  generic(bits: Uint8Array, width: number, height: number, dpi: number): Uint8Array;
  /** Starts symbol mode for a whole document. `threshold` is how alike two shapes must be to share one (0.4 to 0.9). */
  symbolBegin(threshold: number, dpi: number): void;
  symbolAdd(bits: Uint8Array, width: number, height: number): void;
  /** Ends collecting pages: the shared symbol table, then each page's own data by number (in the order added). */
  symbolFinish(): { globals: Uint8Array; page: (index: number) => Uint8Array };
  symbolEnd(): void;
}

interface Exports {
  memory: WebAssembly.Memory;
  _initialize?: () => void;
  wasm_alloc(n: number): number;
  wasm_free(p: number): void;
  out_len(): number;
  out_ptr(): number;
  enc_generic(bits: number, w: number, h: number, xres: number, yres: number): number;
  sym_begin(thresh: number, xres: number, yres: number): number;
  sym_add(bits: number, w: number, h: number): void;
  sym_globals(): number;
  sym_page(n: number): number;
  sym_end(): void;
}

/** Just enough of the WASI system interface for the encoder's C library: no files, no clock beyond zero, messages dropped. */
function wasiImports(getMemory: () => WebAssembly.Memory): WebAssembly.Imports {
  const view = () => new DataView(getMemory().buffer);
  const table: Record<string, (...a: number[]) => number> = {
    fd_write: (_fd, iovs, iovsLen, nwritten) => {
      const v = view();
      let total = 0;
      for (let i = 0; i < iovsLen; i++) total += v.getUint32(iovs + i * 8 + 4, true);
      v.setUint32(nwritten, total, true);
      return 0;
    },
    clock_time_get: (_id, _prec, out) => { const v = view(); v.setUint32(out, 0, true); v.setUint32(out + 4, 0, true); return 0; },
    environ_sizes_get: (count, size) => { const v = view(); v.setUint32(count, 0, true); v.setUint32(size, 0, true); return 0; },
    environ_get: () => 0,
    args_sizes_get: (count, size) => { const v = view(); v.setUint32(count, 0, true); v.setUint32(size, 0, true); return 0; },
    args_get: () => 0,
    random_get: (ptr, len) => { new Uint8Array(getMemory().buffer, ptr, len).fill(0); return 0; },
    proc_exit: () => { throw new Error("The JBIG2 encoder stopped unexpectedly."); },
    fd_close: () => 0,
    fd_prestat_get: () => 8, // EBADF: no preopened folders, which ends the C library's scan
    fd_prestat_dir_name: () => 8,
    fd_seek: () => 70, // ESPIPE
    fd_fdstat_get: () => 8, // EBADF
  };
  const wasi = new Proxy(table, { get: (t, k: string) => t[k] ?? (() => 52) }); // 52 = ENOSYS for anything else
  return { wasi_snapshot_preview1: wasi as unknown as WebAssembly.ModuleImports };
}

/** Starts the encoder from the wasm file's bytes. */
export async function createJbig2(wasm: BufferSource): Promise<Jbig2Encoder> {
  let memory: WebAssembly.Memory | undefined;
  const imports = wasiImports(() => memory!);
  const inst = (await WebAssembly.instantiate(wasm, imports)).instance;
  const x = inst.exports as unknown as Exports;
  memory = x.memory;
  x._initialize?.();
  const heap = () => new Uint8Array(x.memory.buffer);
  const put = (bits: Uint8Array) => { const ptr = x.wasm_alloc(bits.length); heap().set(bits, ptr); return ptr; };
  const take = () => heap().slice(x.out_ptr(), x.out_ptr() + x.out_len());
  const enc: Jbig2Encoder = {
    generic(bits, w, h, dpi) {
      const ptr = put(bits);
      try { x.enc_generic(ptr, w, h, dpi, dpi); } finally { x.wasm_free(ptr); }
      return take();
    },
    symbolBegin(threshold, dpi) { if (!x.sym_begin(threshold, dpi, dpi)) throw new Error("JBIG2 symbol mode could not start"); },
    symbolAdd(bits, w, h) {
      const ptr = put(bits);
      try { x.sym_add(ptr, w, h); } finally { x.wasm_free(ptr); }
    },
    symbolFinish() {
      x.sym_globals();
      const globals = take();
      return { globals, page: (i: number) => { x.sym_page(i); return take(); } };
    },
    symbolEnd() { x.sym_end(); },
  };
  return enc;
}

/** Packs RGBA pixels into JBIG2 rows: a pixel darker than `threshold` (0-255 grey) is black. */
export function packBitmap(rgba: Uint8ClampedArray, width: number, height: number, threshold = 150): Uint8Array {
  const rb = (width + 7) >> 3;
  const out = new Uint8Array(rb * height);
  for (let y = 0; y < height; y++) {
    const o = y * rb;
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      const p = row + x * 4;
      // Green is a good stand-in for brightness, and the pages are already near grey.
      if (rgba[p + 1] < threshold) out[o + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  return out;
}
